// tslint:disable
import { currencies } from "./constants";

declare global {
    interface String {
        format(...texts: string[]): string;
        toCamelCase(): string;
        toPascalCase(): string;
        toKebabCase(): string;
        toSentence(): string;
        removeSpaces(): string;
        capitalize(): string;
        addSlashes(): string;
        isEqual(str: string): boolean;
        equalsAny(...strings: string[]): boolean;
        startsWith(str: string, position?: number): boolean;
        /**
         * polyfill for string.endsWith from MDN:  https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/endsWith
         * @param str 
         * @param position 
         */
        endsWith(str: string, position?: number): boolean;
        replaceAll(find: string, replace: string): string;
        repeat(count: number): string;
    }

    interface Number {
        /**
         * rounding to a certain number of decimals
         */
        rounder(precision: number): number;
        formatNumber(pattern: string): string;
        format(args: INumberFormatArgs): string;
        stripScientificNotation(): string;
    }

    interface INumberFormatArgs {
        formatType: string; //eFormatType: none, number, currency, percentage
        minPrecision: number; //the minimum number of decimal places.  If the min and max are same, you can just set min
        maxPrecision?: number; //the maximum number of decimal places
        currency?: string; //the currency iso
        thousandsSeparator?: string;
        decimalSeparator?: string;
        prefix?: string;
        suffix?: string;
    }

    interface Array<T> {
        clear(): void;
        remove(item: T): Array<T>;
        /**
         * Conditionally removes items from an array and returns a new array with the removed items.
         */
        removeWhere(predicate: (item: T) => boolean): Array<T>;
        contains(item: T): boolean;
        pushArray(items: T[]): void;
        /**
         * If the condition is true, then pushes the given items into the array.
         * Either way it returns the array in a fluent style.
         * @param condition 
         * @param items 
         */
        pushIf(condition: boolean, ...items: T[]): Array<T>;
        equals(a: T[]): boolean;
        /**
         * Useful in cases where you want to find the length of an array that is not zero-indexed and has holes in its keys.
         * E.g. if you have an array like this:
         * [1] => "foo"
         * [3] => "bar"
         *
         * array.length would return 4 (since it fills in [0] and [2] with undefined values). array.getDefinedLength will return
         * 2, which is the number of "filled in" values. Note that this only discounts truly undefined values. Nulls, zeros, and
         * empty strings will still be counted.
         */
        getDefinedLength(): number;
        move(fromIndex, toIndex): Array<T>;
        removeNulls(): Array<T>;
        find(predicate: (element: T, index: number, array: T[]) => boolean): T;
        first(): T;
        last(): T;
        next(item: T, predicate?: (item: T) => boolean): T;
        previous(item: T, predicate?: (item: T) => boolean): T;
        sortBy(predicate: (element: T) => any): void;
        sortByDescending(predicate: (element: T) => any): void;
        fill(value: any, start?: number, end?: number);
        unique(selector?: (item: T) => any): T[];
        top(n: number, predicate: (element: T, index: number) => boolean): Array<T>;
        innerJoin<TOther>(otherArr: TOther[], keySelector: (item: T) => any, otherKeySelector: (item: TOther) => any): any[];
        leftJoin<TOther>(otherArr: TOther[], keySelector: (item: T) => any, otherKeySelector: (item: TOther) => any): any[];
        rightJoin<TOther>(otherArr: TOther[], keySelector: (item: T) => any, otherKeySelector: (item: TOther) => any): any[];
        recurse<T>(childSelector: (item: T) => T[], action: (item: T, parent: T) => void): void;
        toObject(keySelector: (item: T) => string | number): { [key: string]: T };
        flatten<U>(childSelector: (item: T) => U[]): U[];
        selectMany<TChild>(selector: (item: T) => TChild[] | TChild): TChild[];
        hasDuplicateBy(selector?: (item: T) => any): boolean;
        groupBy<TResult = any>(
            keySelector: Array<{ name: string, selector: (item: T) => any }>,
            aggregates?: {
                count?: Array<{ name: string, selector: ((item: T) => any) | '*' }>;
                sum?: Array<{ name: string, selector: (item: T) => number }>;
                avg?: Array<{ name: string, selector: (item: T) => number }>;
                min?: Array<{ name: string, selector: (item: T) => any }>;
                max?: Array<{ name: string, selector: (item: T) => any }>;
            }
        ): TResult[];
    }

    interface ObjectConstructor {
        assign(target: any, ...sources: any[]): any;
        values(o: {}): any[];
    }
}


String.prototype.replaceAll = function (find, replace) {
    return this.replace(new RegExp(find, 'g'), replace);
}

String.prototype.format = function (...texts: string[]) {
    return this.replace(/\{\{|\}\}|\{(\d+)\}/g, function (m, n) {
        if (m == "{{") { return "{"; }
        if (m == "}}") { return "}"; }
        return texts[n];
    });
};

String.prototype.toCamelCase = function () {
    return this.replace(/\w\S*/g, function (txt) { return txt.charAt(0).toLowerCase() + txt.substr(1); });
};

String.prototype.toPascalCase = function () {
    return this.replace(/\w\S*/g, function (txt) { return txt.charAt(0).toUpperCase() + txt.substr(1); });
};

String.prototype.capitalize = function () {
    return this.charAt(0).toUpperCase() + this.slice(1);
}

const kebabCaseRegex = /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g;
String.prototype.toKebabCase = function () {
    const matches = this.match(kebabCaseRegex);
    if (!matches) return this;
    return matches.map(x => x.toLowerCase())
        .join('-');
}

String.prototype.toSentence = function () {
    return this.replace(/([A-Z])/g, ' $1') // insert a space before all caps            
        .replace(/^./, function (str) { return str.toUpperCase(); })// uppercase the first character
}

String.prototype.removeSpaces = function () {
    return this.replace(/\s+/g, '');
}

String.prototype.addSlashes = function () {
    return this.replace(/\\/g, '\\\\').
        //replace(/\u0008/g, '\\b').
        replace(/\t/g, '\\t').
        replace(/\n/g, '\\n').
        replace(/\f/g, '\\f').
        replace(/\r/g, '\\r').
        replace(/'/g, '\\\'').
        replace(/"/g, '\\"');
}

String.prototype.isEqual = function (str: string) {
    return (this ? this.toUpperCase() : undefined) === (str ? str.toUpperCase() : undefined);
}

String.prototype.equalsAny = function (...strings: string[]) {
    return strings && strings.some(s => s.isEqual(this));
}

if (!String.prototype.endsWith) {
    String.prototype.endsWith = function (searchString, position) {
        var subjectString = this.toString();
        if (position === undefined || position > subjectString.length) {
            position = subjectString.length;
        }
        position -= searchString.length;
        var lastIndex = subjectString.indexOf(searchString, position);
        return lastIndex !== -1 && lastIndex === position;
    };
}

if (!String.prototype.startsWith) {
    String.prototype.startsWith = function (searchString, position) {
        position = position || 0;
        return this.indexOf(searchString, position) === position;
    };
}

if (!String.prototype.repeat) {
    String.prototype.repeat = function (count) {
        'use strict';
        if (this == null) {
            throw new TypeError('can\'t convert ' + this + ' to object');
        }
        var str = '' + this;
        count = +count;
        if (count != count) {
            count = 0;
        }
        if (count < 0) {
            throw new RangeError('repeat count must be non-negative');
        }
        if (count == Infinity) {
            throw new RangeError('repeat count must be less than infinity');
        }
        count = Math.floor(count);
        if (str.length == 0 || count == 0) {
            return '';
        }
        // Ensuring count is a 31-bit integer allows us to heavily optimize the
        // main part. But anyway, most current (August 2014) browsers can't handle
        // strings 1 << 28 chars or longer, so:
        if (str.length * count >= 1 << 28) {
            throw new RangeError('repeat count must not overflow maximum string size');
        }
        var maxCount = str.length * count;
        count = Math.floor(Math.log(count) / Math.log(2));
        while (count) {
            str += str;
            count--;
        }
        str += str.substring(0, maxCount - str.length);
        return str;
    }
}

Number.prototype.rounder = function (precision: number) {
    const num = this.stripScientificNotation();
    return +(Math.round(+(num + 'e' + precision)) + 'e-' + precision);
}

/**
 * Converts scientific notation to decimal or big number string.
 */
Number.prototype.stripScientificNotation = function(): string {
    let strNum = this.toString();
    
    if (strNum.indexOf("e") === -1) return strNum;
    const strNumArr = strNum.split("e");
    if (strNumArr.length === 2) {
        let formattedNum = strNumArr[0].replace(".", "");
        let numZeros;
        if (strNumArr[1].charAt(0) === "-") {
            let isNegative = strNumArr[0].charAt(0) === "-";
            if (isNegative) {
                formattedNum = formattedNum.substr(1);
            }
            numZeros = parseInt(strNumArr[1].substr(1), 10) - 1;
            if (numZeros > 50) return "0"; //Let's not bother with extremely small numbers
            for(let i = 0; i < numZeros; i++) {
                formattedNum = "0" + formattedNum;
            }
            formattedNum = "0." + formattedNum;
            if (isNegative) {
                formattedNum = "-" + formattedNum;
            }
        } else {
            // Then maybe a very large number, ex: 5.302923e+22
            numZeros =  parseInt(strNumArr[1].substr(1), 10) - (formattedNum.length -1);
            if (numZeros > 50) return "Infinity"; //Let's not bother with extremely large numbers
            for(let i = 0; i < numZeros; i++) {
                formattedNum += "0";
            }
        }

        return formattedNum;
    }
    return "0";
}


/**
 * formats a number given different formatting options
 */
Number.prototype.format = function (args: INumberFormatArgs) {
    var pattern: string;
    var t = args.thousandsSeparator;
    var d = args.decimalSeparator || ".";
    var type = args.formatType;
    var currency = args.currency || "USD";
    var minPrecision: number = isNaN(args.minPrecision) ? 0 : args.minPrecision;
    var maxPrecision: number = isNaN(args.maxPrecision) ? 0 : args.maxPrecision;
    maxPrecision = maxPrecision < minPrecision ? minPrecision : maxPrecision;
    var prefix = args.prefix || "";
    var suffix = args.suffix || "";

    //for currency, we should always have a thousands separator
    if (type === "currency") {
        t = t || ",";
    }
    if (type === "number" || type === "currency" || type === "percentage") {
        pattern = "#" + t + "##0" + d + Array(minPrecision + 1).join("0") + Array(maxPrecision - minPrecision + 1).join("#");
    }

    var v = (<number>this).formatNumber(pattern);

    if (type === "currency") {
        // Not sure why it's not picking up the currencies declaration in icons
        var symbol = currencies[args.currency] || "";
        v = symbol + v + " " + args.currency;
    }
    if (type === "percentage") {
        v += "%";
    }
    
    return prefix + v + suffix;
}

/**
 * formats a number using a pattern: see here for documentation: https://code.google.com/p/javascript-number-formatter/
 */
Number.prototype.formatNumber = function (pattern: string) {
    var m: any = pattern;
    var v: any = this;
    var num: number = <number>v;

    // Make sure it's not in scientific notation
    // (Javascript switches automatically when you get to 7 decimal places)
    if (num < 1e-6) v = num.stripScientificNotation();

    if (!m || isNaN(+v)) {
        return v; //return as it is.
    }
    //convert any string to number according to formation sign.
    v = m.charAt(0) == '-' ? -v : +v;
    var isNegative = v < 0 ? v = -v : 0; //process only abs(), and turn on flag.

    //search for separator for grp & decimal, anything not digit, not +/- sign, not #.
    var result = m.match(/[^\d\-\+#]/g);
    var Decimal = (result && result[result.length - 1]) || '.'; //treat the right most symbol as decimal 
    var Group = (result && result[1] && result[0]) || ',';  //treat the left most symbol as group separator

    //split the decimal for the format string if any.
    var m = m.split(Decimal);
    //Fix the decimal first, toFixed will auto fill trailing zero.
    v = v.toFixed(m[1] && m[1].length);
    v = +(v) + ''; //convert number to string to trim off *all* trailing decimal zero(es)

    //fill back any trailing zero according to format
    var pos_trail_zero = m[1] && m[1].lastIndexOf('0'); //look for last zero in format
    var part = v.split('.');
    //integer will get !part[1]
    if (!part[1] || part[1] && part[1].length <= pos_trail_zero) {
        v = (+v).toFixed(pos_trail_zero + 1);
    }
    var szSep = m[0].split(Group); //look for separator
    m[0] = szSep.join(''); //join back without separator for counting the pos of any leading 0.

    var pos_lead_zero = m[0] && m[0].indexOf('0');
    if (pos_lead_zero > -1) {
        while (part[0].length < (m[0].length - pos_lead_zero)) {
            part[0] = '0' + part[0];
        }
    }
    else if (+part[0] == 0) {
        part[0] = '';
    }

    v = v.split('.');
    v[0] = part[0];

    //process the first group separator from decimal (.) only, the rest ignore.
    //get the length of the last slice of split result.
    var pos_separator = (szSep[1] && szSep[szSep.length - 1].length);
    if (pos_separator) {
        var integer = v[0];
        var str = '';
        var offset = integer.length % pos_separator;
        for (var i = 0, l = integer.length; i < l; i++) {

            str += integer.charAt(i); //ie6 only support charAt for sz.
            //-pos_separator so that won't trail separator on full length
            if (!((i - offset + 1) % pos_separator) && i < l - pos_separator) {
                str += Group;
            }
        }
        v[0] = str;
    }

    v[1] = (m[1] && v[1]) ? Decimal + v[1] : "";
    return (isNegative ? '-' : '') + v[0] + v[1]; //put back any negation and combine integer and fraction.
}

Object.defineProperty(Array.prototype, "clear", {
    enumerable: false,
    writable: true,
    value: function (): void {
        this.length = 0;
    }
});
//Array.prototype.clear = function (): void {
//    this.length = 0;
//}

Object.defineProperty(Array.prototype, "remove", {
    enumerable: false,
    writable: true,
    value: function (item: any): Array<any> {
        var index = this.indexOf(item);
        if (index != -1) {
            return this.splice(this.indexOf(item), 1);
        }
    }
});
//Array.prototype.remove = function (item: any): Array<any> {
//    var index = this.indexOf(item);
//    if (index != -1) {
//        return this.splice(this.indexOf(item), 1);
//    }
//}


Object.defineProperty(Array.prototype, "removeWhere", {
    enumerable: false,
    writable: true,
    value: function (predicate) {
        var removed = [];
        for (var i = this.length - 1; i >= 0; i--) {
            var item = this[i];
            if (predicate(item)) {
                removed.pushArray(this.splice(i, 1));
            }
        }
        return removed;
    }
});
//Array.prototype.removeWhere = function(predicate) {
//    for (var i = this.length - 1; i >= 0; i--) {
//        var item = this[i];
//        if (predicate(item)) {
//            this.splice(i, 1);
//        }
//    }
//}


Object.defineProperty(Array.prototype, "move", {
    enumerable: false,
    writable: true,
    value: function (fromIndex, toIndex) {
        if (toIndex >= this.length) {
            var k = toIndex - this.length;
            while ((k--) + 1) {
                this.push(undefined);
            }
        }
        this.splice(toIndex, 0, this.splice(fromIndex, 1)[0]);
        return this; // for testing purposes
    }
});
//Array.prototype.move = function (fromIndex, toIndex) {
//    if (toIndex >= this.length) {
//        var k = toIndex - this.length;
//        while ((k--) + 1) {
//            this.push(undefined);
//        }
//    }
//    this.splice(toIndex, 0, this.splice(fromIndex, 1)[0]);
//    return this; // for testing purposes
//};


Object.defineProperty(Array.prototype, "contains", {
    enumerable: false,
    writable: true,
    value: function (item: any): boolean {
        var index = this.indexOf(item);
        return (index != -1);
    }
});
//Array.prototype.contains = function (item: any): boolean {
//    var index = this.indexOf(item);
//    return (index != -1);
//}


Object.defineProperty(Array.prototype, "pushArray", {
    enumerable: false,
    writable: true,
    value: function (items: any[]): void {
        this.push(...items);
    }
});
//Array.prototype.pushArray = function (items: any[]): void {
//    this.push.apply(this, items);
//}

Object.defineProperty(Array.prototype, "pushIf", {
    enumerable: false,
    writable: true,
    value: function (condition: boolean, ...items: any[]): any[] {
        if (condition) this.push(...items);
        return this;
    }
});


Object.defineProperty(Array.prototype, "equals", {
    enumerable: false,
    writable: true,
    value: function (b) {
        if (this === b) return true;
        if (b == null) return false;
        if (this.length != b.length) return false;

        // If you don't care about the order of the elements inside
        // the array, you should sort both arrays here.

        for (var i = 0; i < this.length; ++i) {
            if (this[i] !== b[i]) return false;
        }
        return true;
    }
});

if (!Array.prototype.find) {
    Object.defineProperty(Array.prototype, "find", {
        enumerable: false,
        writable: true,
        value: function (predicate) {
            if (this == null) {
                throw new TypeError('Array.prototype.find called on null or undefined');
            }
            if (typeof predicate !== 'function') {
                throw new TypeError('predicate must be a function');
            }
            var list = Object(this);
            var length = list.length >>> 0;
            var thisArg = arguments[1];
            var value;

            for (var i = 0; i < length; i++) {
                value = list[i];
                if (predicate.call(thisArg, value, i, list)) {
                    return value;
                }
            }
            return undefined;
        }
    });
}



Object.defineProperty(Array.prototype, "getDefinedLength", {
    enumerable: false,
    writable: true,
    value: function () {
        var result = 0;
        for (var i = 0; i < this.length; i++) {
            if (this[i] !== undefined) result++;
        }
        return result;
    }
});

Object.defineProperty(Array.prototype, "first", {
    enumerable: false,
    writable: true,
    value: function () {
        var item;
        if (this.length) {
            item = this[0];
        }
        return item;
    }
});

Object.defineProperty(Array.prototype, "last", {
    enumerable: false,
    writable: true,
    value: function () {
        if (this.length) {
            return this[this.length - 1];
        }
        return undefined;
    }
});

Object.defineProperty(Array.prototype, "next", {
    enumerable: false,
    writable: true,
    value: function (item, predicate?: (item) => boolean) {
        var next;
        var i = this.indexOf(item);
        if (i >= -1 && i < this.length - 1) {
            if (predicate) {
                for (var n = i + 1; n < this.length; n++) {
                    if (predicate(this[n])) {
                        next = this[n];
                        break;
                    }
                }
            } else {
                next = this[i + 1];
            }
        }
        return next;
    }
});

Object.defineProperty(Array.prototype, "previous", {
    enumerable: false,
    writable: true,
    value: function (item, predicate?: (item) => boolean) {
        var prev;
        var i = this.indexOf(item);
        if (i == -1) i = this.length;
        if (i > 0 && i <= this.length) {
            if (predicate) {
                for (var n = i - 1; n >= 0; n--) {
                    if (predicate(this[n])) {
                        prev = this[n];
                        break;
                    }
                }
            } else {
                prev = this[i - 1];
            }
        }
        return prev;
    }
});

Object.defineProperty(Array.prototype, "removeNulls", {
    enumerable: false,
    writable: true,
    value: function () {
        return this.filter(function (x) {
            return x !== null && x !== undefined;
        });
    }
});

Object.defineProperty(Array.prototype, "sortBy", {
    enumerable: false,
    writable: true,
    value: function (predicate) {
        return this.sort((a, b) => {
            if (predicate(a) < predicate(b)) {
                return -1;
            } else if (predicate(a) > predicate(b)) {
                return 1;
            } else {
                return 0;
            }
        });
    }
});

Object.defineProperty(Array.prototype, "sortByDescending", {
    enumerable: false,
    writable: true,
    value: function (predicate) {
        return this.sort((a, b) => {
            if (predicate(a) > predicate(b)) {
                return -1;
            } else if (predicate(a) < predicate(b)) {
                return 1;
            } else {
                return 0;
            }
        });
    }
});

if (!Array.prototype.fill) {
    Object.defineProperty(Array.prototype, "fill", {
        enumerable: false,
        writable: true,
        value: function (value) {

            // Steps 1-2.
            if (this == null) {
                throw new TypeError('this is null or not defined');
            }

            var O = Object(this);

            // Steps 3-5.
            var len = O.length >>> 0;

            // Steps 6-7.
            var start = arguments[1];
            var relativeStart = start >> 0;

            // Step 8.
            var k = relativeStart < 0 ?
                Math.max(len + relativeStart, 0) :
                Math.min(relativeStart, len);

            // Steps 9-10.
            var end = arguments[2];
            var relativeEnd = end === undefined ?
                len : end >> 0;

            // Step 11.
            var final = relativeEnd < 0 ?
                Math.max(len + relativeEnd, 0) :
                Math.min(relativeEnd, len);

            // Step 12.
            while (k < final) {
                O[k] = value;
                k++;
            }

            // Step 13.
            return O;
        }
    });    
}

Object.defineProperty(Array.prototype, "unique", {
    enumerable: false,
    writable: true,
    value: function (selector) {
        var u = {}, a = [];
        for (var i = 0, l = this.length; i < l; ++i) {
            var val = this[i];
            if (selector) val = selector(val);

            if (u.hasOwnProperty(val)) {
                continue;
            }
            a.push(this[i]);
            u[val] = 1;
        }
        return a;
    }
});

Object.defineProperty(Array.prototype, "top", {
    enumerable: false,
    writable: true,
    value: function (n, predicate) {
        if (this == null) {
            throw new TypeError('Array.prototype.find called on null or undefined');
        }
        if (typeof predicate !== 'function') {
            throw new TypeError('predicate must be a function');
        }
        var length = this.length;
        var thisArg = arguments[1];
        var value;

        var matches = [];
        for (var i = 0; i < length; i++) {
            value = this[i];
            if (predicate.call(thisArg, value, i)) {
                matches.push(value);
                if (matches.length >= n) {
                    return matches;
                }
            }
        }
        return matches;
    }
});

Object.defineProperty(Array.prototype, "innerJoin", {
    enumerable: false,
    writable: true,
    value: function (otherArr, keySelector, otherKeySelector) {
        return ArrayExt.innerJoin(this, otherArr, keySelector, otherKeySelector);
    }
});

Object.defineProperty(Array.prototype, "leftJoin", {
    enumerable: false,
    writable: true,
    value: function (otherArr, keySelector, otherKeySelector) {
        return ArrayExt.leftJoin(this, otherArr, keySelector, otherKeySelector);
    }
});

Object.defineProperty(Array.prototype, "rightJoin", {
    enumerable: false,
    writable: true,
    value: function (otherArr, keySelector, otherKeySelector) {
        return ArrayExt.leftJoin(otherArr, this, keySelector, otherKeySelector);
    }
});

Object.defineProperty(Array.prototype, "fullOuterJoin", {
    enumerable: false,
    writable: true,
    value: function (otherArr, keySelector, otherKeySelector) {
        return ArrayExt.fullOuterJoin(otherArr, this, keySelector, otherKeySelector);
    }
});

Object.defineProperty(Array.prototype, "recurse", {
    enumerable: false,
    writable: true,
    value: function (childSelector: (item) => any[], action: (item) => void) {
        for (var i = 0; i < this.length; i++) {
            action(this[i]);
            var childArr = childSelector(this[i]);
            if (childArr) {
                childArr.recurse(childSelector, action);
            }
        }
    }
});

Object.defineProperty(Array.prototype, "selectMany", {
    enumerable: false,
    writable: true,
    value: function (selector: (item) => any[]) {
        return this.reduce((out, v) => {
            let vres = selector(v);
            if (vres != null){
                if (!Array.isArray(vres)) vres = [vres];
                out.push(...vres);
            }
            
            return out;
        }, []);
    }
});

Object.defineProperty(Array.prototype, "hasDuplicateBy", {
    enumerable: false,
    writable: true,
    value: function (selector) {
        let seen = new Set<any>();
        for (let item of this) {
            let k = selector(item);
            if (seen.has(k)) return true;
            seen.add(k);
        }
        return false;
    }
});

Object.defineProperty(Array.prototype, "groupBy", {
    enumerable: false,
    writable: true,
    value: function (keySelector, aggregates?) {
        const groups = new Map();

        if (!keySelector || keySelector.length == 0) {
            // single group if no key selector specified
            groups.set("key1", this);
        } else {
            // group items
            for (const item of this) {
                // create composite key
                let key = JSON.stringify(keySelector.map(ks => ks.selector(item)));

                if (!groups.has(key)) {
                    groups.set(key, []);
                }
                groups.get(key).push(item);
            }
        }

        const result = [];

        for (const [key, items] of Array.from(groups.entries())) {
            const group: any = {};

            // add selector properties
            if (keySelector && keySelector.length > 0) {
                keySelector.forEach(ks => {
                    group[ks.name] = ks.selector(items[0]);
                });
            }

            // add aggregate properties
            if (aggregates) {
                // count
                if (aggregates.count) {
                    aggregates.count.forEach(agg => {
                        if (agg.selector === '*') {
                            // COUNT(*) - count all rows
                            group[agg.name] = items.length;
                        } else {
                            // COUNT(column) - count rows where selector returns non-null value
                            const filtered = items.filter(item => {
                                const value = agg.selector(item);
                                return value !== null && value !== undefined;
                            });
                            group[agg.name] = filtered.length;
                        }
                    });
                }

                // sum
                if (aggregates.sum) {
                    aggregates.sum.forEach(agg => {
                        group[agg.name] = items.reduce((sum, item) => sum + (agg.selector(item) || 0), 0);
                    });
                }

                // avg
                if (aggregates.avg) {
                    aggregates.avg.forEach(agg => {
                        const values = items.map(agg.selector).filter(v => v != null);
                        group[agg.name] = values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
                    });
                }

                // min
                if (aggregates.min) {
                    aggregates.min.forEach(agg => {
                        const values = items.map(agg.selector).filter(v => v != null);
                        if (values.length === 0) {
                            group[agg.name] = null;
                        } else {
                            group[agg.name] = values.reduce((min, v) => v < min ? v : min, values[0]);
                        }
                    });
                }

                // max
                if (aggregates.max) {
                    aggregates.max.forEach(agg => {
                        const values = items.map(agg.selector).filter(v => v != null);
                        if (values.length === 0) {
                            group[agg.name] = null;
                        } else {
                            group[agg.name] = values.reduce((max, v) => v > max ? v : max, values[0]);
                        }
                    });
                }
            }

            result.push(group);
        }

        return result;
    }
});

export class ArrayExt {
    static innerJoin<T, TOther>(
        thisArr: T[],
        otherArr: TOther[],
        keySelector: (item: T) => any,
        otherKeySelector: (item: TOther) => any): Array<T & TOther> {

        var arrSmall: any[];
        var dbBig = {}; //dictionary of the bigger array indexed by key
        var smallKeySelector: (item) => any;
        if (thisArr.length <= otherArr.length) {
            arrSmall = thisArr;
            smallKeySelector = keySelector;
            //fill in the big db
            otherArr.forEach(i => {
                var key = otherKeySelector(i);
                dbBig[key] = dbBig[key] || [];
                dbBig[key].push(i);
            });

        } else {
            arrSmall = otherArr;
            smallKeySelector = otherKeySelector;
            //fill in the big db
            thisArr.forEach(i => {
                var key = keySelector(i);
                dbBig[key] = dbBig[key] || [];
                dbBig[key].push(i);
            });
        }

        var result: Array<T & TOther> = [];
        arrSmall.forEach(item => {
            var key = smallKeySelector(item);
            if (dbBig.hasOwnProperty(key)) {
                dbBig[key].forEach(i => {
                    //create a new object to leave the source arrays as is
                    result.push(Object.assign({}, item, i));
                });
            }
        });

        return result;
    }

    static leftJoin<T, TOther>(
        thisArr: T[],
        otherArr: TOther[],
        keySelector: (item: T) => any,
        otherKeySelector: (item: TOther) => any): Array<T & TOther> {

        var dbOther = {};
        otherArr.forEach(i => {
            var key = otherKeySelector(i);
            dbOther[key] = dbOther[key] || [];
            dbOther[key].push(i);
        });

        var result: Array<T & TOther> = [];
        thisArr.forEach(item => {
            var key = keySelector(item);
            if (dbOther[key]) {
                dbOther[key].forEach(i => {
                    //create a new object to leave the source arrays as is
                    result.push(Object.assign({}, item, i));
                });
            } else {
                result.push(Object.assign({}, item));
            }
        });

        return result;
    }

    static fullOuterJoin<T, TOther>(
        thisArr: T[],
        otherArr: TOther[],
        keySelector: (item: T) => any,
        otherKeySelector: (item: TOther) => any): Array<T & TOther> {
        
        var arrSmall: any[];
        var dbBig = {}; //dictionary of the bigger array indexed by key
        var smallKeySelector: (item) => any;
        if (thisArr.length <= otherArr.length) {
            arrSmall = thisArr;
            smallKeySelector = keySelector;
            //fill in the big db
            otherArr.forEach(i => {
                var key = otherKeySelector(i);
                dbBig[key] = dbBig[key] || [];
                dbBig[key].push(i);
            });

        } else {
            arrSmall = otherArr;
            smallKeySelector = otherKeySelector;
            //fill in the big db
            thisArr.forEach(i => {
                var key = keySelector(i);
                dbBig[key] = dbBig[key] || [];
                dbBig[key].push(i);
            });
        }

        var result: Array<T & TOther> = [];
        arrSmall.forEach(item => {
            var key = smallKeySelector(item);
            if (dbBig.hasOwnProperty(key)) {
                dbBig[key].forEach(i => {
                    //create a new object to leave the source arrays as is
                    result.push(Object.assign({}, item, i));
                });
                // Delete the item from dbBig... anything remaining in this
                // list will be added at the end.
                delete dbBig[key];
            }
            else {
                // The item is not in the "big" array, but we add it to the
                // result as part of the outer join.
                result.push(Object.assign({}, item));
            }
        });

        // Now we just add the items left over from the big array that
        // didn't match the keys.
        for (let key in dbBig) {
            let item = dbBig[key];
            item.forEach(i => result.push(Object.assign({}, i)));
        }

        return result;
    }
}

Object.defineProperty(Array.prototype, "toObject", {
    enumerable: false,
    writable: true,
    value: function (keySelector: (item) => number | string) {
        return this.reduce((o, item) => {
            o[keySelector(item)] = item;
            return o;
        }, {});
    }
});

Object.defineProperty(Array.prototype, "flatten", {
    enumerable: false,
    writable: true,
    value: function (childSelector: (item) => any[]) {
        let r = [];
        let f = (item) => {
            r.push(item);
            let children = childSelector(item);
            if (children) {
                for (let i of children) {
                    f(i);
                }
            }
        };
        for (let i of this) f(i);        
        
        return r;
    }
});

if (!Object.assign) {
    Object.defineProperty(Object, 'assign', {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (target) {
            'use strict';
            if (target === undefined || target === null) {
                throw new TypeError('Cannot convert first argument to object');
            }

            var to = Object(target);
            for (var i = 1; i < arguments.length; i++) {
                var nextSource = arguments[i];
                if (nextSource === undefined || nextSource === null) {
                    continue;
                }
                nextSource = Object(nextSource);

                var keysArray = Object.keys(nextSource);
                for (var nextIndex = 0, len = keysArray.length; nextIndex < len; nextIndex++) {
                    var nextKey = keysArray[nextIndex];
                    var desc = Object.getOwnPropertyDescriptor(nextSource, nextKey);
                    if (desc !== undefined && desc.enumerable) {
                        to[nextKey] = nextSource[nextKey];
                    }
                }
            }
            return to;
        }
    });
}

if (!Object.values) {
    Object.defineProperty(Object, 'values', {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (o: {}) {
            'use strict';
            return Object.keys(o).map(key => o[key]);
        }
    });
}

if (!('toJSON' in Error.prototype)) {
    Object.defineProperty(Error.prototype, 'toJSON', {
        value: function () {
            var alt = {};

            Object.getOwnPropertyNames(this).forEach(function (key) {
                alt[key] = this[key];
            }, this);

            return alt;
        },
        configurable: true,
        writable: true
    });
}
